import React from 'react';
import { Link, useMatch } from 'react-router-dom';
import { Home, ListChecks } from 'lucide-react';
import type { PublisherConfig } from '../../types/selfService';
import { displayedName } from '../../data/publisherConfigs';
import { useAuth } from './auth/useAuth';
import HeaderAuth from './auth/HeaderAuth';

interface PublicHeaderProps {
  /** Optional publisher — when present, renders publisher logo + name on the left
   *  and the brand-tinted hairline below the bar. Without a publisher, the bar
   *  is plain (used by My Campaigns / Browse / Detail pages). */
  publisher?: PublisherConfig;
  /** Eyebrow shown above the title on non-publisher pages (e.g. "All publishers"). */
  eyebrow?: string;
  /** Main title for non-publisher pages (e.g. "My Campaigns"). */
  title?: string;
  /** Optional right-side content (e.g. a Back link). When omitted, only HeaderAuth shows. */
  rightSlot?: React.ReactNode;
}

function tintFromAccent(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return `rgba(59,130,246,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const PublicHeader: React.FC<PublicHeaderProps> = ({ publisher, eyebrow, title, rightSlot }) => {
  const accent = publisher?.accentColor || publisher?.primaryColor;
  const { isAuthenticated } = useAuth();
  // Closed-loop CTA: landing → "My Campaigns"; my-campaigns → "Home";
  // library/my-team → both. Anywhere else → no CTA (no global root link
  // inside the loop).
  const landingMatch = useMatch('/:slug');
  const startMatch = useMatch('/:slug/start/*');
  const myCampaignsMatch = useMatch('/:slug/my-campaigns');
  const myCampaignDetailMatch = useMatch('/:slug/my-campaigns/:submissionId');
  const submittedMatch = useMatch('/:slug/submitted/:submissionId');
  const myTeamMatch = useMatch('/:slug/my-team');
  // The Library page does NOT get a top-bar tab — dropdown-only — per
  // CTV-457 Phase B nav-placement decision. We still capture the slug so
  // HeaderAuth's dropdown can build well-formed URLs and so My Campaigns /
  // Home back-links render at the top of the page.
  const libraryMatch = useMatch('/:slug/library');
  const helpMatch = useMatch('/:slug/help');
  // CTV-591: the Settings surface is slug-scoped (`/:slug/settings/*`),
  // with sub-routes for Business, Team, and Security. Treat it as the
  // Library/Team surface class so the closed-loop chrome — Home + My
  // Campaigns CTAs, and HeaderAuth's slug-gated dropdown links — renders
  // everywhere under it.
  const settingsMatch = useMatch('/:slug/settings/*');
  const slug =
    landingMatch?.params.slug ??
    startMatch?.params.slug ??
    myCampaignsMatch?.params.slug ??
    myCampaignDetailMatch?.params.slug ??
    submittedMatch?.params.slug ??
    myTeamMatch?.params.slug ??
    libraryMatch?.params.slug ??
    helpMatch?.params.slug ??
    settingsMatch?.params.slug ??
    null;
  const onLanding = !!(landingMatch || startMatch);
  const onMyCampaigns = !!(myCampaignsMatch || myCampaignDetailMatch);
  const onLibraryOrTeam = !!(libraryMatch || myTeamMatch || helpMatch || settingsMatch);

  return (
    <header className="sticky top-0 z-40 bg-white/75 backdrop-blur-xl">
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-2.5 md:py-2 flex items-center gap-3">
        {publisher ? (
          (() => {
            const label = displayedName(publisher);
            return (
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Link
                  to={`/${publisher.slug}`}
                  aria-label={`${label} home`}
                  className="flex items-center min-w-0 group"
                >
                  {publisher.logoUrl ? (
                    <img
                      src={publisher.logoUrl}
                      alt={label}
                      className={`h-8 md:h-10 w-auto object-contain ${publisher.logoInvert === true ? 'brightness-0 invert' : ''}`}
                      style={
                        publisher.logoScale && publisher.logoScale !== 1
                          ? { transform: `scale(${publisher.logoScale})`, transformOrigin: 'left center' }
                          : undefined
                      }
                    />
                  ) : (
                    <div
                      className="h-7 w-7 md:h-8 md:w-8 rounded-md flex items-center justify-center text-white font-semibold text-sm shrink-0"
                      style={{ backgroundColor: accent }}
                    >
                      {label.charAt(0)}
                    </div>
                  )}
                </Link>
                <div className="hidden sm:block h-5 w-px bg-slate-200" />
                <a
                  href="https://ctvbuyer.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 hidden sm:inline-flex items-center gap-2 font-sans text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 origin-left hover:scale-110 transition-transform"
                  aria-label="Powered by CTV Buyer"
                >
                  <span>Powered by</span>
                  <img
                    src={`${import.meta.env.BASE_URL}ctvbuyer-logo.svg`}
                    alt="CTV Buyer"
                    className="h-5 md:h-6 w-auto object-contain -translate-y-0.5"
                    style={{ filter: 'brightness(0)' }}
                  />
                </a>
              </div>
            );
          })()
        ) : (
          <div className="flex-1 min-w-0">
            {eyebrow && (
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                {eyebrow}
              </div>
            )}
            {title && (
              <h1 className="text-base md:text-lg font-semibold tracking-tight text-slate-900 leading-tight">
                {title}
              </h1>
            )}
          </div>
        )}

        {rightSlot}
        {slug && (onLanding || onLibraryOrTeam) && isAuthenticated && (
          <Link
            to={`/${slug}/my-campaigns`}
            className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500 hover:text-slate-900 px-3 py-1.5 rounded-full hover:bg-slate-100 transition-colors"
            aria-label="My Campaigns"
          >
            <ListChecks className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">My Campaigns</span>
          </Link>
        )}
        {slug && (onMyCampaigns || onLibraryOrTeam) && (
          <Link
            to={`/${slug}`}
            className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500 hover:text-slate-900 px-3 py-1.5 rounded-full hover:bg-slate-100 transition-colors"
            aria-label="Home"
          >
            <Home className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Home</span>
          </Link>
        )}
        <HeaderAuth isLightHeader slug={slug} />
      </div>

      {accent && (
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-0 left-0 right-0 h-px"
          style={{
            background: `linear-gradient(90deg, transparent, ${tintFromAccent(accent, 0.55)}, transparent)`,
          }}
        />
      )}
      {!accent && <div className="border-b border-slate-200/70" />}
    </header>
  );
};

export default PublicHeader;
